import Auth from '@aws-amplify/auth';
import { Lambda } from '@aws-sdk/client-lambda';
import { addSnackbar, snackbarTypes } from '../../../utils/snackbar';

const uploadTypeConfig = {
  'customer-channel': {
    uploadPrefix: 'customer-mapping/upload/',
    messages: {
      timeout: {
        header: 'Data generation timed out',
        text: 'It is taking too long to process the uploaded customer list. Please try again or upload a smaller list.',
      },
      successWithFailures: {
        header: 'Data generation successful',
        text: 'One or more assessment mappings failed.\nPlease download the failure report for details.',
      },
      success: {
        header: 'Data generation successful',
        text: 'Assessment mappings have been updated.',
      },
      catchError: {
        header: 'Data generation failed',
        text: 'An error occurred while generating data',
      },
    },
  },
  'channel-survey': {
    uploadPrefix: 'channel-survey/upload/',
    messages: {
      timeout: {
        header: 'Upload timed out',
        text: 'It is taking too long to process the channel survey mapping file. Please try again or upload a smaller file.',
      },
      successWithFailures: {
        header: 'Upload successful',
        text: 'One or more channel survey mappings failed.\nPlease download the failure report for details.',
      },
      success: {
        header: 'Upload successful',
        text: 'Channel survey mappings have been updated.',
      },
      catchError: {
        header: 'Upload failed',
        text: 'An error occurred while processing the channel survey mapping file',
      },
    },
  },
};

const invokeGenerateDataLambda = async (
  filePath,
  uploadType = 'customer-channel'
) => {
  const { uploadPrefix, messages } = uploadTypeConfig[uploadType];

  const credentials = await Auth.currentCredentials();

  const lambda = new Lambda({
    credentials: Auth.essentialCredentials(credentials),
    region: 'eu-west-1',
  });

  const userBucketLocation = `private/${credentials.identityId}/`;

  const session = await Auth.currentSession();
  const payload = JSON.stringify({
    userBucketLocation,
    jwt: session.idToken.jwtToken,
    filePath: `${uploadPrefix}${filePath}`,
    uploadType,
  });

  return lambda
    .invoke({
      LogType: 'None',
      InvocationType: 'RequestResponse',
      Payload: payload,
      FunctionName: process.env.LAMBDA_GENERATE_DATA_FUNCTION,
    })
    .then((response) => {
      const stringResponse = new TextDecoder('utf-8').decode(response.Payload);
      const generateResponse = JSON.parse(stringResponse);
      if (generateResponse.errorMessage) {
        if (generateResponse.errorMessage.includes('Task timed out')) {
          addSnackbar({
            header: messages.timeout.header,
            text: messages.timeout.text,
            type: snackbarTypes.ERROR,
          });
        }
      }
      if (generateResponse.complete) {
        if (generateResponse.failures && generateResponse.failures.length > 0) {
          addSnackbar({
            header: messages.successWithFailures.header,
            text: messages.successWithFailures.text,
            type: snackbarTypes.WARNING,
          });
        } else {
          addSnackbar({
            header: messages.success.header,
            text: messages.success.text,
            type: snackbarTypes.SUCCESS,
          });
        }
      } else {
        if (generateResponse.failures && generateResponse.failures.length > 0) {
          addSnackbar({
            header: 'Something went wrong',
            text:
              generateResponse.failures[0].detail ?? 'Please contact support',
            type: snackbarTypes.ERROR,
          });
        }
      }
    })
    .catch((error) => {
      addSnackbar({
        header: messages.catchError.header,
        text: messages.catchError.text,
        type: snackbarTypes.ERROR,
      });
      throw error;
    });
};

export { invokeGenerateDataLambda };
